import {
  formatSaved,
  savingsClaim,
  type AdminRule,
  type AuditEntry,
  type BetaReport,
  type BetaReportFile,
  type BetaReportStatus,
  type BetaSettings,
  type BetaSettingsResponse,
  type BetaSignup,
  type ProGrant,
  type SiteMode,
} from '@nolens/domain';
import { formatNumber, type Locale } from '@nolens/i18n';
import {
  Badge,
  Banner,
  Button,
  EmptyState,
  Field,
  Input,
  SegmentedControl,
  Select,
  Textarea,
  useLocale,
  useToast,
} from '@nolens/ui';
import { useEffect, useState, type FormEvent, type ReactNode } from 'react';
import { QueryView } from '../components/Query';
import { inPeriod, PeriodSelect, SavingsTable, type Period } from '../components/Savings';
import { apiBlob, ApiError } from '../lib/api';
import {
  useAdminAudit,
  useAdminBetaReports,
  useAdminBetaSettings,
  useAdminBetaSignups,
  useAdminGrants,
  useAdminOverview,
  useAdminRules,
  useAdminSavings,
  useBetaActions,
  useBetaReportActions,
  useGrantActions,
  useRuleActions,
} from '../lib/queries';
import { WithMe } from './workspace';
import { shellText } from '../i18n/shell';
import { adminText } from '../i18n/admin';

type Tab = 'resumo' | 'acessos' | 'sites' | 'registos' | 'inscricoes' | 'anomalias';

const dateTime = (iso: string, locale: Locale) =>
  new Date(iso).toLocaleString(locale, { dateStyle: 'short', timeStyle: 'short' });
const date = (iso: string, locale: Locale) =>
  new Date(iso).toLocaleDateString(locale, { dateStyle: 'medium' });

/**
 * Portal de administração do proprietário da aplicação. A API confirma, a cada pedido, que
 * a conta está em app.platform_admins; esta página só esconde o que a API recusaria.
 */
export function AdminPage() {
  const [tab, setTab] = useState<Tab>('resumo');
  const t = adminText(useLocale());
  const st = shellText(useLocale());
  return (
    <WithMe crumbs={() => [{ label: st.admin }]}>
      {(me) =>
        !me.isPlatformAdmin ? (
          <EmptyState title={t.noAccess} description={t.noAccessHint} />
        ) : (
          <section>
            <h1 className="text-2xl font-semibold tracking-tight">{t.title}</h1>
            <p className="mt-1 text-sm text-text-2">{t.hint}</p>
            <div className="mt-5">
              <SegmentedControl
                label={t.section}
                value={tab}
                onChange={setTab}
                options={[
                  { value: 'resumo', label: t.tabSummary },
                  { value: 'acessos', label: t.tabAccess },
                  { value: 'sites', label: t.tabSites },
                  { value: 'registos', label: t.tabLogs },
                  { value: 'inscricoes', label: t.tabSignups },
                  { value: 'anomalias', label: t.tabReports },
                ]}
              />
            </div>
            <div className="mt-6">
              {tab === 'resumo' && <Summary />}
              {tab === 'acessos' && <Access />}
              {tab === 'sites' && <Rules />}
              {tab === 'registos' && <Audit />}
              {tab === 'inscricoes' && <BetaProgram />}
              {tab === 'anomalias' && <Reports />}
            </div>
          </section>
        )
      }
    </WithMe>
  );
}

// ── Resumo e poupança estimada ───────────────────────────────────────────

function Tile({ label, value, hint }: { label: string; value: ReactNode; hint?: string }) {
  return (
    <div className="rounded-[14px] bg-surface-1 px-4 py-3.5 shadow-[inset_0_0_0_1px_var(--border)]">
      <p className="text-[12.5px] font-medium text-text-3">{label}</p>
      <p className="mt-1 text-[22px] font-semibold tracking-tight">{value}</p>
      {hint && <p className="mt-0.5 text-[12px] text-text-3">{hint}</p>}
    </div>
  );
}

/** Só totais da plataforma: nenhum espaço, email ou número por pessoa (ADR-G18). */
function Summary() {
  const overview = useAdminOverview();
  const savings = useAdminSavings();
  const [period, setPeriod] = useState<Period>('all');
  const locale = useLocale();
  const t = adminText(locale);
  const n = (v: number) => formatNumber(locale, v);
  return (
    <QueryView q={overview}>
      {(o) => (
        <>
          <div className="grid gap-3 sm:grid-cols-3 lg:grid-cols-6" data-admin-overview>
            <Tile
              label={t.estimatedSavings}
              value={formatSaved(o.savedSeconds, locale)}
              hint={t.stepsTimes(n(o.steps))}
            />
            <Tile label={t.documentedSteps} value={n(o.steps)} />
            <Tile label={t.guides} value={n(o.guides)} />
            <Tile label={t.accounts} value={n(o.users)} />
            <Tile label={t.teams} value={n(o.teams)} />
            <Tile label={t.unlocks30} value={o.unlocks30d} />
          </div>
          <p className="mt-2 text-[12.5px] text-text-3">
            {savingsClaim(locale)} {t.totalsNote}
          </p>

          <div className="mt-8 flex flex-wrap items-end gap-3">
            <h2 className="mr-auto text-lg font-semibold">{t.savingsByMonth}</h2>
            <PeriodSelect value={period} onChange={setPeriod} />
          </div>
          <QueryView q={savings}>
            {(rows) => (
              <SavingsTable
                first={t.month}
                months
                rows={rows
                  .filter((r) => inPeriod(r.month, period))
                  .map((r) => ({ key: r.month, label: r.month, guides: r.guides, steps: r.steps }))}
              />
            )}
          </QueryView>
        </>
      )}
    </QueryView>
  );
}

// ── Sites ────────────────────────────────────────────────────────────────

function Rules() {
  const rules = useAdminRules();
  const act = useRuleActions();
  const toast = useToast();
  const t = adminText(useLocale());
  const [form, setForm] = useState<{ pattern: string; mode: SiteMode; note: string }>({
    pattern: '',
    mode: 'protected',
    note: '',
  });
  const [filter, setFilter] = useState('');
  const error = [act.create, act.update, act.remove]
    .map((m) => m.error)
    .find((e) => e instanceof ApiError) as ApiError | undefined;

  const add = (e: FormEvent) => {
    e.preventDefault();
    act.create.mutate(form, {
      onSuccess: () => {
        setForm({ pattern: '', mode: 'protected', note: '' });
        toast(t.ruleAdded, 'positive');
      },
    });
  };

  return (
    <>
      <p className="max-w-3xl text-[14px] text-text-2">{t.rulesHint}</p>
      {error && (
        <Banner tone="critical" className="mt-4">
          {error.code === 'CONFLICT' ? t.ruleExists : error.message}
        </Banner>
      )}
      <form onSubmit={add} className="mt-4 flex flex-wrap items-end gap-2" aria-label={t.newRule}>
        <Input
          aria-label={t.site}
          className="!h-10 w-64"
          placeholder={t.sitePlaceholder}
          value={form.pattern}
          onChange={(e) => setForm({ ...form, pattern: e.target.value })}
        />
        <Select
          aria-label={t.restriction}
          className="!h-10 w-auto"
          value={form.mode}
          onChange={(e) => setForm({ ...form, mode: e.target.value as SiteMode })}
        >
          <option value="protected">{t.protected}</option>
          <option value="locked">{t.locked}</option>
        </Select>
        <Input
          aria-label={t.note}
          className="!h-10 w-56"
          placeholder={t.noteOptional}
          maxLength={200}
          value={form.note}
          onChange={(e) => setForm({ ...form, note: e.target.value })}
        />
        <Button type="submit" loading={act.create.isPending} disabled={!form.pattern.trim()}>
          {t.add}
        </Button>
      </form>
      <QueryView q={rules}>
        {(list) => {
          const shown = list.filter((r) =>
            (r.pattern + ' ' + r.note).toLowerCase().includes(filter.toLowerCase()),
          );
          return (
            <>
              <div className="mt-6 flex items-center gap-3">
                <h2 className="mr-auto text-lg font-semibold">{t.rulesCount(list.length)}</h2>
                <Input
                  aria-label={t.searchRules}
                  className="!h-9 w-56 text-sm"
                  placeholder={t.search}
                  value={filter}
                  onChange={(e) => setFilter(e.target.value)}
                />
              </div>
              <ul className="mt-3 flex flex-col gap-2" data-rules={list.length}>
                {shown.map((r) => (
                  <RuleRow key={r.id} rule={r} act={act} />
                ))}
              </ul>
            </>
          );
        }}
      </QueryView>
    </>
  );
}

function RuleRow({ rule, act }: { rule: AdminRule; act: ReturnType<typeof useRuleActions> }) {
  const [note, setNote] = useState(rule.note);
  const [confirm, setConfirm] = useState(false);
  const toast = useToast();
  const t = adminText(useLocale());
  return (
    <li
      className="flex flex-wrap items-center gap-x-3 gap-y-2 rounded-[12px] bg-surface-1 px-4 py-2.5 shadow-[inset_0_0_0_1px_var(--border)]"
      data-rule={rule.pattern}
    >
      <span className="min-w-[200px] flex-1 truncate font-medium">{rule.pattern}</span>
      <Select
        aria-label={t.restrictionOf(rule.pattern)}
        className="!h-9 w-auto text-sm"
        value={rule.mode}
        title={t.modeLong(rule.mode)}
        onChange={(e) =>
          act.update.mutate(
            { id: rule.id, mode: e.target.value as SiteMode },
            { onSuccess: () => toast(t.ruleUpdated, 'positive') },
          )
        }
      >
        <option value="protected">{t.protected}</option>
        <option value="locked">{t.locked}</option>
      </Select>
      <Input
        aria-label={t.noteOf(rule.pattern)}
        className="!h-9 w-48 text-sm"
        maxLength={200}
        value={note}
        onChange={(e) => setNote(e.target.value)}
        onBlur={() =>
          note !== rule.note &&
          act.update.mutate(
            { id: rule.id, note },
            { onSuccess: () => toast(t.noteSaved, 'positive') },
          )
        }
      />
      {confirm ? (
        <span
          className="flex items-center gap-2 text-[13px]"
          role="alertdialog"
          aria-label={t.removeOf(rule.pattern)}
        >
          {t.removeRule}
          <Button size="sm" variant="ghost" onClick={() => setConfirm(false)}>
            {t.cancel}
          </Button>
          <Button
            size="sm"
            variant="danger"
            loading={act.remove.isPending}
            onClick={() =>
              act.remove.mutate(rule.id, { onSuccess: () => toast(t.ruleRemoved, 'positive') })
            }
          >
            {t.remove}
          </Button>
        </span>
      ) : (
        <Button size="sm" variant="ghost" onClick={() => setConfirm(true)}>
          {t.remove}
        </Button>
      )}
    </li>
  );
}

// ── Acessos (testes sem pagamento) ───────────────────────────────────────

/**
 * Dar o Pro a quem testa e prolongar o teste das equipas, sem SQL. As funções da base de
 * dados voltam a confirmar que quem pede é o proprietário.
 */
function Access() {
  const act = useGrantActions();
  const grants = useAdminGrants();
  const locale = useLocale();
  const t = adminText(locale);
  const toast = useToast();
  const [pro, setPro] = useState({ email: '', days: '30' });
  const [team, setTeam] = useState({ email: '', days: '30' });
  const error = [act.pro, act.team, act.revoke]
    .map((m) => m.error)
    .find((e) => e instanceof ApiError) as ApiError | undefined;
  const errorText = (e: ApiError) =>
    e.code === 'NOT_FOUND'
      ? act.team.error === e
        ? t.noTeam
        : t.noAccount
      : e.code === 'CONFLICT'
        ? t.paidPlan
        : e.message;
  const days = (v: string) => Math.min(365, Math.max(1, Math.round(Number(v)) || 0));

  const givePro = (e: FormEvent) => {
    e.preventDefault();
    act.team.reset();
    act.pro.mutate(
      { email: pro.email.trim(), days: days(pro.days) },
      {
        onSuccess: (r) => {
          toast(t.proGiven(pro.email.trim(), date(r.until, locale)), 'positive');
          setPro({ ...pro, email: '' });
        },
      },
    );
  };
  const extendTeam = (e: FormEvent) => {
    e.preventDefault();
    act.pro.reset();
    act.team.mutate(
      { email: team.email.trim(), days: days(team.days) },
      {
        onSuccess: (r) => {
          toast(t.teamExtended(r.teams ?? 0, date(r.until, locale)), 'positive');
          setTeam({ ...team, email: '' });
        },
      },
    );
  };

  const form = (
    label: string,
    hint: string,
    submit: string,
    v: { email: string; days: string },
    set: (v: { email: string; days: string }) => void,
    onSubmit: (e: FormEvent) => void,
    pending: boolean,
    kind: string,
  ) => (
    <form
      onSubmit={onSubmit}
      aria-label={label}
      data-grant={kind}
      className="rounded-[14px] bg-surface-1 p-4 shadow-[inset_0_0_0_1px_var(--border)]"
    >
      <h2 className="font-semibold">{label}</h2>
      <p className="mt-1 text-[13px] text-text-2">{hint}</p>
      <div className="mt-3 flex flex-wrap items-end gap-2">
        <Input
          aria-label={t.email}
          type="email"
          required
          className="!h-10 !w-auto min-w-[180px] flex-1"
          placeholder={t.emailPlaceholder}
          value={v.email}
          onChange={(e) => set({ ...v, email: e.target.value })}
        />
        <Input
          aria-label={t.days}
          type="number"
          min={1}
          max={365}
          className="!h-10 !w-24"
          value={v.days}
          onChange={(e) => set({ ...v, days: e.target.value })}
        />
        <Button type="submit" loading={pending} disabled={!v.email.trim()}>
          {submit}
        </Button>
      </div>
    </form>
  );

  return (
    <>
      <p className="max-w-3xl text-[14px] text-text-2">{t.accessHint}</p>
      {error && (
        <Banner tone="critical" className="mt-4">
          {errorText(error)}
        </Banner>
      )}
      <div className="mt-4 grid gap-3 md:grid-cols-2">
        {form(
          t.giveProTitle,
          t.giveProHint,
          t.givePro,
          pro,
          setPro,
          givePro,
          act.pro.isPending,
          'pro',
        )}
        {form(
          t.teamTitle,
          t.teamHint,
          t.extendTeam,
          team,
          setTeam,
          extendTeam,
          act.team.isPending,
          'team',
        )}
      </div>
      <h2 className="mt-8 text-lg font-semibold">{t.grantsTitle}</h2>
      <QueryView q={grants}>
        {(list) =>
          list.length ? (
            <ul className="mt-3 flex flex-col gap-2" data-grants={list.length}>
              {list.map((g) => (
                <GrantRow key={g.email} grant={g} act={act} />
              ))}
            </ul>
          ) : (
            <p className="mt-2 text-text-2">{t.noGrants}</p>
          )
        }
      </QueryView>
    </>
  );
}

function GrantRow({ grant, act }: { grant: ProGrant; act: ReturnType<typeof useGrantActions> }) {
  const [confirm, setConfirm] = useState(false);
  const locale = useLocale();
  const t = adminText(locale);
  const toast = useToast();
  const ended =
    grant.status === 'trialing' && (!grant.trialEndsAt || new Date(grant.trialEndsAt) < new Date());
  return (
    <li
      className="flex flex-wrap items-center gap-x-3 gap-y-2 rounded-[12px] bg-surface-1 px-4 py-2.5 shadow-[inset_0_0_0_1px_var(--border)]"
      data-grant-email={grant.email}
    >
      <span className="min-w-[200px] flex-1 truncate font-medium">{grant.email}</span>
      <span className={'text-[13px] tabular-nums ' + (ended ? 'text-text-3' : 'text-text-2')}>
        {grant.trialEndsAt ? `${t.until} ${date(grant.trialEndsAt, locale)}` : grant.status}
        {ended && ` (${t.expired})`}
      </span>
      {confirm ? (
        <span
          className="flex items-center gap-2 text-[13px]"
          role="alertdialog"
          aria-label={t.revokeOf(grant.email)}
        >
          {t.revokeConfirm(grant.email)}
          <Button size="sm" variant="ghost" onClick={() => setConfirm(false)}>
            {t.cancel}
          </Button>
          <Button
            size="sm"
            variant="danger"
            loading={act.revoke.isPending}
            onClick={() =>
              act.revoke.mutate(grant.email, { onSuccess: () => toast(t.revoked, 'positive') })
            }
          >
            {t.revoke}
          </Button>
        </span>
      ) : (
        <Button size="sm" variant="ghost" onClick={() => setConfirm(true)}>
          {t.revoke}
        </Button>
      )}
    </li>
  );
}

// ── Registos ─────────────────────────────────────────────────────────────

function detailText(a: AuditEntry, t: ReturnType<typeof adminText>, locale: Locale): string {
  const d = a.detail as {
    until?: string;
    mode?: SiteMode;
    from?: { mode?: SiteMode; note?: string; pattern?: string };
    to?: { mode?: SiteMode; note?: string; pattern?: string };
  };
  const mode = (m?: SiteMode) => (m ? t.modeShort(m) : '');
  if (a.action === 'rule.update' && d.from && d.to) {
    const parts: string[] = [];
    if (d.from.pattern !== d.to.pattern) parts.push(`${d.from.pattern} → ${d.to.pattern}`);
    if (d.from.mode !== d.to.mode) parts.push(`${mode(d.from.mode)} → ${mode(d.to.mode)}`);
    if (d.from.note !== d.to.note) parts.push(t.noteChanged);
    return parts.join(' · ');
  }
  if (d.until) return `${t.until} ${date(d.until, locale)}`;
  return mode(d.mode);
}

// ── Programa beta: link da extensão, boas-vindas e acessos ───────────────

/** Os marcadores do texto de boas-vindas (como a API os substitui), para a pré-visualização. */
const fillWelcome = (text: string, v: Record<string, string>) =>
  text.replace(
    /\{(nome|email|link_extensao|link_entrar|link_reportar)\}/g,
    (_, k: string) => v[k]!,
  );

function BetaSettingsCard() {
  const q = useAdminBetaSettings();
  return <QueryView q={q}>{(s) => <BetaSettingsForm key={s.updatedAt} initial={s} />}</QueryView>;
}

function BetaSettingsForm({ initial }: { initial: BetaSettingsResponse }) {
  const locale = useLocale();
  const t = adminText(locale);
  const toast = useToast();
  const act = useBetaActions();
  const [lang, setLang] = useState<Locale>(locale);
  const [form, setForm] = useState<BetaSettings>({
    extensionUrl: initial.extensionUrl,
    macUrl: initial.macUrl,
    welcome: initial.welcome,
  });
  const dirty =
    form.extensionUrl !== initial.extensionUrl ||
    form.macUrl !== initial.macUrl ||
    JSON.stringify(form.welcome) !== JSON.stringify(initial.welcome);
  const text = form.welcome[lang];
  const setText = (k: 'subject' | 'body', v: string) =>
    setForm({ ...form, welcome: { ...form.welcome, [lang]: { ...text, [k]: v } } });
  const vars = {
    nome: t.previewName,
    email: 'ana@empresa.pt',
    link_extensao: form.extensionUrl || t.extensionUrlPlaceholder,
    link_entrar: `${window.location.origin}/entrar`,
    link_reportar: `${window.location.origin}/reportar/…`,
  };
  const error = [act.save.error, act.welcome.error].find((e) => e instanceof ApiError) as
    ApiError | undefined;
  const save = (e: FormEvent) => {
    e.preventDefault();
    act.save.mutate(form, { onSuccess: () => toast(t.saved, 'positive') });
  };
  return (
    <form
      onSubmit={save}
      className="rounded-[14px] bg-surface-1 p-4 shadow-[inset_0_0_0_1px_var(--border)]"
      aria-label={t.betaSettingsTitle}
      data-beta-settings
    >
      <h2 className="font-semibold">{t.betaSettingsTitle}</h2>
      <p className="mt-1 max-w-3xl text-[13px] text-text-2">{t.betaSettingsHint}</p>
      {error && (
        <Banner tone="critical" className="mt-3">
          {error.code === 'NOT_CONFIGURED' ? t.notConfigured : error.message}
        </Banner>
      )}
      <div className="mt-4 grid gap-4">
        <Field label={t.extensionUrl}>
          {(p) => (
            <Input
              {...p}
              type="url"
              placeholder={t.extensionUrlPlaceholder}
              value={form.extensionUrl}
              onChange={(e) => setForm({ ...form, extensionUrl: e.target.value })}
            />
          )}
        </Field>
        <Field label={t.macUrl} hint={t.macUrlHint}>
          {(p) => (
            <Input
              {...p}
              type="url"
              placeholder={t.macUrlPlaceholder}
              value={form.macUrl}
              onChange={(e) => setForm({ ...form, macUrl: e.target.value })}
              data-mac-url
            />
          )}
        </Field>
        <SegmentedControl
          label={t.language}
          value={lang}
          onChange={setLang}
          options={[
            { value: 'pt-PT', label: t.langPt },
            { value: 'en', label: t.langEn },
          ]}
        />
        <div className="grid gap-4 lg:grid-cols-2">
          <div className="grid min-w-0 content-start gap-3">
            <Field label={t.subject}>
              {(p) => (
                <Input
                  {...p}
                  maxLength={200}
                  value={text.subject}
                  onChange={(e) => setText('subject', e.target.value)}
                />
              )}
            </Field>
            <Field label={t.body} hint={t.markers}>
              {(p) => (
                <Textarea
                  {...p}
                  rows={16}
                  maxLength={10000}
                  className="font-mono text-[13px]"
                  value={text.body}
                  onChange={(e) => setText('body', e.target.value)}
                />
              )}
            </Field>
          </div>
          <div className="min-w-0">
            <p className="text-sm font-medium">{t.preview}</p>
            <div className="mt-1.5 rounded-[12px] bg-surface-0 p-4 text-[14px] shadow-[inset_0_0_0_1px_var(--border)]">
              <p className="font-semibold">{fillWelcome(text.subject, vars)}</p>
              <p className="mt-3 whitespace-pre-wrap text-text-2 [overflow-wrap:anywhere]">
                {fillWelcome(text.body, vars)}
              </p>
            </div>
          </div>
        </div>
      </div>
      <div className="mt-4 flex flex-wrap items-center gap-2">
        <Button type="submit" loading={act.save.isPending} disabled={!dirty}>
          {t.save}
        </Button>
        <Button
          variant="ghost"
          onClick={() =>
            setForm({ ...form, welcome: { ...form.welcome, [lang]: initial.defaults[lang] } })
          }
        >
          {t.restoreDefault}
        </Button>
        <Button
          variant="secondary"
          loading={act.welcome.isPending && !!act.welcome.variables?.test}
          disabled={dirty || !initial.extensionUrl}
          title={dirty ? t.unsaved : !initial.extensionUrl ? t.noExtensionUrl : undefined}
          onClick={() =>
            act.welcome.mutate(
              { test: true },
              {
                onSuccess: (r) =>
                  toast(r.sent ? t.testSent : t.sentResult(0, 1), r.sent ? 'positive' : 'critical'),
              },
            )
          }
        >
          {t.sendTest}
        </Button>
        {dirty && <span className="text-[13px] text-text-3">{t.unsaved}</span>}
      </div>
    </form>
  );
}

/** Inscrições para testar (página /teste): quem é, a conta, as boas-vindas e os acessos. */
function BetaProgram() {
  const q = useAdminBetaSignups();
  const settings = useAdminBetaSettings();
  const act = useBetaActions();
  const grants = useGrantActions();
  const locale = useLocale();
  const t = adminText(locale);
  const toast = useToast();
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [confirm, setConfirm] = useState<{ ids?: string[]; pending?: boolean; n: number } | null>(
    null,
  );
  const [days, setDays] = useState('30');
  const ready = !!settings.data?.extensionUrl;
  const grantDays = Math.min(365, Math.max(1, Math.round(Number(days)) || 0));
  const error = [act.welcome.error, grants.pro.error, grants.team.error].find(
    (e) => e instanceof ApiError,
  ) as ApiError | undefined;

  const send = (b: { ids?: string[]; pending?: boolean }) =>
    act.welcome.mutate(b, {
      onSuccess: (r) => {
        toast(t.sentResult(r.sent, r.failed), r.failed ? 'critical' : 'positive');
        setSelected(new Set());
        setConfirm(null);
      },
      onError: () => setConfirm(null),
    });
  const givePro = (b: BetaSignup) => {
    grants.team.reset();
    grants.pro.mutate(
      { email: b.email, days: grantDays },
      { onSuccess: (r) => toast(t.proGiven(b.email, date(r.until, locale)), 'positive') },
    );
  };
  const giveTeam = (b: BetaSignup) => {
    grants.pro.reset();
    grants.team.mutate(
      { email: b.email, days: grantDays, create: true, teamName: b.company },
      { onSuccess: (r) => toast(t.teamGiven(!!r.created, date(r.until, locale)), 'positive') },
    );
  };
  const access = (b: BetaSignup) =>
    [
      b.proLive && (b.proUntil ? t.proUntil(date(b.proUntil, locale)) : t.proActive),
      b.teamLive && (b.teamUntil ? t.teamUntil(date(b.teamUntil, locale)) : t.teamActive),
    ]
      .filter(Boolean)
      .join(' · ') || t.free;

  return (
    <div className="grid gap-6">
      <BetaSettingsCard />
      <QueryView q={q}>
        {(list) => {
          const pending = list.filter((b) => !b.welcomeSentAt);
          const all = list.length > 0 && selected.size === list.length;
          const toggle = (id: string) => {
            const next = new Set(selected);
            if (next.has(id)) next.delete(id);
            else next.add(id);
            setSelected(next);
          };
          return list.length ? (
            <div className="grid gap-3">
              <div className="flex flex-wrap items-center gap-2">
                <p className="mr-auto text-sm text-text-2">{t.signupsHint(list.length)}</p>
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() =>
                    void navigator.clipboard
                      .writeText(list.map((b) => b.email).join(', '))
                      .then(() => toast(t.copied, 'positive'))
                  }
                >
                  {t.copyEmails}
                </Button>
              </div>
              <div className="flex flex-wrap items-center gap-2" data-beta-send>
                <Button
                  size="sm"
                  disabled={!ready || !selected.size}
                  title={!ready ? t.noExtensionUrl : undefined}
                  onClick={() => setConfirm({ ids: [...selected], n: selected.size })}
                >
                  {t.sendSelected(selected.size)}
                </Button>
                <Button
                  size="sm"
                  variant="secondary"
                  disabled={!ready || !pending.length}
                  title={!ready ? t.noExtensionUrl : undefined}
                  onClick={() => setConfirm({ pending: true, n: pending.length })}
                >
                  {t.sendPending(pending.length)}
                </Button>
                <label className="ml-auto flex items-center gap-2 text-[13px] text-text-2">
                  {t.grantDays}
                  <Input
                    type="number"
                    min={1}
                    max={365}
                    className="!h-9 !w-20 text-sm"
                    value={days}
                    onChange={(e) => setDays(e.target.value)}
                  />
                </label>
              </div>
              {!ready && <Banner tone="attention">{t.noExtensionUrl}</Banner>}
              {confirm && (
                <div
                  role="alertdialog"
                  aria-label={t.confirmSend(confirm.n)}
                  className="flex flex-wrap items-center gap-2 rounded-[12px] bg-accent-soft px-4 py-2.5 text-[14px]"
                >
                  <span className="mr-auto">{t.confirmSend(confirm.n)}</span>
                  <Button size="sm" variant="ghost" onClick={() => setConfirm(null)}>
                    {t.cancel}
                  </Button>
                  <Button
                    size="sm"
                    loading={act.welcome.isPending}
                    onClick={() => send(confirm.pending ? { pending: true } : { ids: confirm.ids })}
                  >
                    {t.send}
                  </Button>
                </div>
              )}
              {error && (
                <Banner tone="critical">
                  {error.code === 'NOT_CONFIGURED'
                    ? t.notConfigured
                    : error.code === 'NOT_FOUND'
                      ? t.noAccount
                      : error.code === 'CONFLICT'
                        ? t.paidPlan
                        : error.message}
                </Banner>
              )}
              <div
                className="overflow-x-auto rounded-[14px] bg-surface-1 shadow-[inset_0_0_0_1px_var(--border)]"
                data-signups={list.length}
              >
                <table className="w-full text-[14px]">
                  <thead className="text-left text-[12.5px] text-text-3">
                    <tr>
                      <th className="px-3 py-2.5">
                        <input
                          type="checkbox"
                          aria-label={t.selectAll}
                          checked={all}
                          onChange={() =>
                            setSelected(all ? new Set() : new Set(list.map((b) => b.id)))
                          }
                        />
                      </th>
                      <th className="px-3 py-2.5 font-medium">{t.when}</th>
                      <th className="px-3 py-2.5 font-medium">{t.person}</th>
                      <th className="px-3 py-2.5 font-medium">{t.teamSize}</th>
                      <th className="px-3 py-2.5 font-medium">{t.area}</th>
                      <th className="px-3 py-2.5 font-medium">{t.account}</th>
                      <th className="px-3 py-2.5 font-medium">{t.welcome}</th>
                      <th className="px-3 py-2.5 font-medium">{t.access}</th>
                      <th className="px-3 py-2.5 font-medium">{t.reportsCol}</th>
                      <th className="px-3 py-2.5 font-medium">{t.actions}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {list.map((b) => (
                      <tr
                        key={b.id}
                        className="border-t border-border align-top"
                        data-signup={b.email}
                      >
                        <td className="px-3 py-2.5">
                          <input
                            type="checkbox"
                            aria-label={t.selectOne(b.email)}
                            checked={selected.has(b.id)}
                            onChange={() => toggle(b.id)}
                          />
                        </td>
                        <td className="whitespace-nowrap px-3 py-2.5 tabular-nums">
                          {dateTime(b.createdAt, locale)}
                        </td>
                        <td className="px-3 py-2.5">
                          <p className="font-medium">{b.email}</p>
                          <p className="text-[13px] text-text-2">
                            {[b.name, b.company, b.role].filter(Boolean).join(' · ')}
                          </p>
                        </td>
                        <td className="px-3 py-2.5">{b.teamSize}</td>
                        <td className="max-w-[240px] px-3 py-2.5">
                          <p>{b.area}</p>
                          <p className="text-[13px] text-text-2">{b.tools}</p>
                        </td>
                        <td className="whitespace-nowrap px-3 py-2.5">
                          <Badge tone={b.hasAccount ? 'positive' : 'neutral'}>
                            {b.hasAccount ? t.yes : t.notYet}
                          </Badge>
                        </td>
                        <td className="whitespace-nowrap px-3 py-2.5 tabular-nums">
                          {b.welcomeSentAt ? (
                            t.sentTimes(dateTime(b.welcomeSentAt, locale), b.welcomeCount)
                          ) : (
                            <span className="text-text-3">{t.notSent}</span>
                          )}
                        </td>
                        <td className="whitespace-nowrap px-3 py-2.5 text-[13px]">{access(b)}</td>
                        <td className="px-3 py-2.5 tabular-nums">{b.reports}</td>
                        <td className="px-3 py-2.5">
                          <div className="flex flex-wrap gap-1.5">
                            <Button
                              size="sm"
                              variant="secondary"
                              disabled={!ready}
                              loading={
                                act.welcome.isPending &&
                                act.welcome.variables?.ids?.length === 1 &&
                                act.welcome.variables.ids[0] === b.id
                              }
                              onClick={() => send({ ids: [b.id] })}
                            >
                              {b.welcomeSentAt ? t.resend : t.sendOne}
                            </Button>
                            <Button
                              size="sm"
                              variant="ghost"
                              disabled={!b.hasAccount}
                              title={b.hasAccount ? undefined : t.needsAccount}
                              loading={
                                grants.pro.isPending && grants.pro.variables?.email === b.email
                              }
                              onClick={() => givePro(b)}
                            >
                              {t.rowGivePro}
                            </Button>
                            <Button
                              size="sm"
                              variant="ghost"
                              disabled={!b.hasAccount}
                              title={b.hasAccount ? undefined : t.needsAccount}
                              loading={
                                grants.team.isPending && grants.team.variables?.email === b.email
                              }
                              onClick={() => giveTeam(b)}
                            >
                              {t.rowGiveTeam}
                            </Button>
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() =>
                                void navigator.clipboard
                                  .writeText(b.reportUrl)
                                  .then(() => toast(t.linkCopied, 'positive'))
                              }
                            >
                              {t.copyReportLink}
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          ) : (
            <p className="text-text-2">{t.noSignups}</p>
          );
        }}
      </QueryView>
    </div>
  );
}

// ── Anomalias reportadas por quem testa ──────────────────────────────────

const SEVERITY_TONE = { blocker: 'critical', annoying: 'attention', suggestion: 'accent' } as const;

/** Anexo privado (pedido com a sessão do proprietário): miniatura das imagens; PDF abre à parte. */
function Attachment({ reportId, file }: { reportId: string; file: BetaReportFile }) {
  const [url, setUrl] = useState<string | null>(null);
  const path = `/v1/admin/beta/reports/${reportId}/files/${file.id}`;
  const isImage = file.contentType.startsWith('image/');
  useEffect(() => {
    if (!isImage) return;
    let alive = true;
    let made: string | null = null;
    apiBlob(path).then(
      (b) => {
        made = URL.createObjectURL(b);
        if (alive) setUrl(made);
      },
      () => {},
    );
    return () => {
      alive = false;
      if (made) URL.revokeObjectURL(made);
    };
  }, [path, isImage]);
  const open = () =>
    void apiBlob(path).then((b) => {
      const u = URL.createObjectURL(b);
      window.open(u, '_blank', 'noopener');
      setTimeout(() => URL.revokeObjectURL(u), 60_000);
    });
  return (
    <button
      type="button"
      onClick={open}
      title={file.name}
      className="flex w-32 flex-col overflow-hidden rounded-[10px] bg-surface-0 text-left text-[12px] shadow-[inset_0_0_0_1px_var(--border)] hover:brightness-95"
    >
      {isImage ? (
        url ? (
          <img src={url} alt={file.name} className="h-20 w-full object-cover" />
        ) : (
          <span className="h-20 w-full bg-surface-2" />
        )
      ) : (
        <span className="flex h-20 w-full items-center justify-center bg-surface-2 font-semibold text-text-2">
          PDF
        </span>
      )}
      <span className="truncate px-2 py-1">{file.name}</span>
    </button>
  );
}

function ReportCard({ r }: { r: BetaReport }) {
  const locale = useLocale();
  const t = adminText(locale);
  const toast = useToast();
  const act = useBetaReportActions();
  const [note, setNote] = useState(r.adminNote);
  const [confirm, setConfirm] = useState(false);
  return (
    <li
      className="rounded-[14px] bg-surface-1 p-4 shadow-[inset_0_0_0_1px_var(--border)]"
      data-report-id={r.id}
    >
      <div className="flex flex-wrap items-start gap-x-3 gap-y-2">
        <div className="min-w-[240px] flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <Badge tone={SEVERITY_TONE[r.severity]}>{t.severity(r.severity)}</Badge>
            <h3 className="font-semibold">{r.title}</h3>
          </div>
          <p className="mt-1 text-[13px] text-text-2">
            {dateTime(r.createdAt, locale)} · {r.name ? `${r.name} · ` : ''}
            <a
              className="underline"
              href={`mailto:${r.email}?subject=${encodeURIComponent(r.title)}`}
            >
              {r.email}
            </a>
          </p>
        </div>
        <Select
          aria-label={t.statusOf(r.title)}
          className="!h-9 !w-auto text-sm"
          value={r.status}
          onChange={(e) =>
            act.update.mutate({ id: r.id, status: e.target.value as BetaReportStatus })
          }
        >
          {(['new', 'reviewing', 'resolved'] as const).map((s) => (
            <option key={s} value={s}>
              {t.status(s)}
            </option>
          ))}
        </Select>
      </div>
      <p className="mt-3 whitespace-pre-wrap break-words text-[14px]">{r.description}</p>
      {r.steps && (
        <>
          <p className="mt-3 text-[12.5px] font-medium text-text-3">{t.steps}</p>
          <p className="mt-0.5 whitespace-pre-wrap break-words text-[14px]">{r.steps}</p>
        </>
      )}
      <dl className="mt-3 grid gap-x-4 gap-y-1 text-[13px] sm:grid-cols-[auto_1fr]">
        {r.pageUrl && (
          <>
            <dt className="text-text-3">{t.where}</dt>
            <dd className="break-all">{r.pageUrl}</dd>
          </>
        )}
        {r.browser && (
          <>
            <dt className="text-text-3">{t.browser}</dt>
            <dd className="break-all text-text-2">{r.browser}</dd>
          </>
        )}
      </dl>
      {r.files.length > 0 && (
        <>
          <p className="mt-3 text-[12.5px] font-medium text-text-3">{t.attachments}</p>
          <div className="mt-1.5 flex flex-wrap gap-2">
            {r.files.map((f) => (
              <Attachment key={f.id} reportId={r.id} file={f} />
            ))}
          </div>
        </>
      )}
      <div className="mt-4 flex flex-wrap items-end gap-2">
        <Textarea
          aria-label={t.adminNoteOf(r.title)}
          placeholder={t.adminNote}
          rows={2}
          maxLength={2000}
          className="min-h-0 flex-1 text-sm"
          value={note}
          onChange={(e) => setNote(e.target.value)}
          onBlur={() =>
            note !== r.adminNote &&
            act.update.mutate(
              { id: r.id, adminNote: note },
              { onSuccess: () => toast(t.noteSaved, 'positive') },
            )
          }
        />
        {confirm ? (
          <span
            className="flex items-center gap-2 text-[13px]"
            role="alertdialog"
            aria-label={t.removeReport}
          >
            {t.removeReport}
            <Button size="sm" variant="ghost" onClick={() => setConfirm(false)}>
              {t.cancel}
            </Button>
            <Button
              size="sm"
              variant="danger"
              loading={act.remove.isPending}
              onClick={() =>
                act.remove.mutate(r.id, { onSuccess: () => toast(t.reportRemoved, 'positive') })
              }
            >
              {t.remove}
            </Button>
          </span>
        ) : (
          <Button size="sm" variant="ghost" onClick={() => setConfirm(true)}>
            {t.remove}
          </Button>
        )}
      </div>
    </li>
  );
}

function Reports() {
  const q = useAdminBetaReports();
  const t = adminText(useLocale());
  const [status, setStatus] = useState<BetaReportStatus | 'all'>('all');
  return (
    <QueryView q={q}>
      {(list) =>
        list.length ? (
          <>
            <div className="flex flex-wrap items-center gap-3">
              <p className="mr-auto text-sm text-text-2">{t.reportsHint(list.length)}</p>
              <Select
                aria-label={t.statusFilter}
                className="!h-9 !w-auto text-sm"
                value={status}
                onChange={(e) => setStatus(e.target.value as BetaReportStatus | 'all')}
              >
                <option value="all">{t.all}</option>
                {(['new', 'reviewing', 'resolved'] as const).map((s) => (
                  <option key={s} value={s}>
                    {`${t.status(s)} (${list.filter((r) => r.status === s).length})`}
                  </option>
                ))}
              </Select>
            </div>
            <ul className="mt-3 flex flex-col gap-3" data-reports={list.length}>
              {list
                .filter((r) => status === 'all' || r.status === status)
                .map((r) => (
                  <ReportCard key={r.id} r={r} />
                ))}
            </ul>
          </>
        ) : (
          <p className="text-text-2">{t.noReports}</p>
        )
      }
    </QueryView>
  );
}

function Audit() {
  const audit = useAdminAudit();
  const locale = useLocale();
  const t = adminText(locale);
  return (
    <QueryView q={audit}>
      {(list) =>
        list.length ? (
          <div
            className="rounded-[14px] bg-surface-1 shadow-[inset_0_0_0_1px_var(--border)]"
            data-audit={list.length}
          >
            <table className="w-full text-[14px]">
              <thead className="text-left text-[12.5px] text-text-3">
                <tr>
                  <th className="px-4 py-2.5 font-medium">{t.when}</th>
                  <th className="px-4 py-2.5 font-medium">{t.who}</th>
                  <th className="px-4 py-2.5 font-medium">{t.what}</th>
                  <th className="px-4 py-2.5 font-medium">{t.target}</th>
                  <th className="px-4 py-2.5 font-medium">{t.detail}</th>
                </tr>
              </thead>
              <tbody>
                {list.map((a) => (
                  <tr key={a.id} className="border-t border-border">
                    <td className="whitespace-nowrap px-4 py-2.5 tabular-nums">
                      {dateTime(a.at, locale)}
                    </td>
                    <td className="px-4 py-2.5">
                      {a.actorEmail ?? <span className="text-text-3">{t.anonymous}</span>}
                    </td>
                    <td className="px-4 py-2.5">{t.action(a.action)}</td>
                    <td className="px-4 py-2.5 font-medium">{a.target}</td>
                    <td className="px-4 py-2.5 text-text-2">{detailText(a, t, locale)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <p className="text-text-2">{t.noLogs}</p>
        )
      }
    </QueryView>
  );
}
